import type { FxReferenceCategory } from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';

import type { ProjectDataLoadItemType } from './project-data-load-control';
import type {
  FxWorkspaceTreeItem,
  FxWorkspaceTreeItemKind,
} from './fx-workspace-tree.types';
import {
  getProjectDataCategoryRootId,
  MissionTreeNodeData,
  MissionTreeNodeType,
  ProjectDataTreeCategory,
} from './project-data-tree-nodes';
import {
  MAP_WORKSPACES_ROOT_NODE_ID,
  WorkspaceTreeItem,
} from './workspace-tree-nodes';

export const FX_MAP_WORKSPACES_REFERENCE_CATEGORY = 'mapWorkspaces' as const;

function referenceCategoryId(
  workspaceId: string,
  category: FxReferenceCategory
): string {
  return `${workspaceId}::references::${category}`;
}

function scopedReferenceNodeId(
  workspaceId: string,
  category: FxReferenceCategory,
  nodeId: string
): string {
  return `${workspaceId}::references::${category}::${nodeId}`;
}

function scopedReferenceParentId(
  workspaceId: string,
  category: FxReferenceCategory,
  parentId: string | undefined,
  categoryParentId: string,
  categoryRootId: string
): string | undefined {
  if (parentId === undefined) {
    return undefined;
  }

  if (parentId === categoryRootId) {
    return categoryParentId;
  }

  return scopedReferenceNodeId(workspaceId, category, parentId);
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 19 > 15 allowed; flagged for later refactor, not fixed here
function getFxReferenceNodeMeta(
  category: ProjectDataTreeCategory,
  node: TreeNodeDefinition<unknown>
): {
  kind: FxWorkspaceTreeItemKind;
  missionId?: string;
  refTrn?: string;
  loadItemType?: ProjectDataLoadItemType;
  showVisibilityToggle?: boolean;
} {
  switch (category) {
    case 'missions': {
      const data = node.data as MissionTreeNodeData | undefined;
      const missionId = data?.mission?.id;

      if (data === undefined) {
        return { kind: 'referenceMission', showVisibilityToggle: false };
      }

      switch (data.type) {
        case MissionTreeNodeType.Mission:
          return {
            kind: 'referenceMission',
            ...(missionId !== undefined ? { missionId } : {}),
            showVisibilityToggle: false,
          };
        case MissionTreeNodeType.Runs:
          return {
            kind: 'referenceRuns',
            ...(missionId !== undefined ? { missionId } : {}),
            showVisibilityToggle: false,
          };
        case MissionTreeNodeType.Run:
          return {
            kind: 'reference',
            ...(missionId !== undefined ? { missionId } : {}),
            refTrn: node.id,
            loadItemType: 'pointCloud',
          };
        case MissionTreeNodeType.Path:
          return {
            kind: 'reference',
            ...(missionId !== undefined ? { missionId } : {}),
            refTrn: node.id,
            loadItemType: 'trajectory',
          };
        default:
          throw new UnreachableCaseError(data.type);
      }
    }
    case 'models':
      if (node.isLeaf === false) {
        return { kind: 'referenceFolder', showVisibilityToggle: false };
      }

      return { kind: 'reference', refTrn: node.id, loadItemType: 'model' };
    case 'pointClouds':
      return { kind: 'reference', refTrn: node.id, loadItemType: 'pointCloud' };
    case 'trajectories':
      return { kind: 'reference', refTrn: node.id, loadItemType: 'trajectory' };
    default:
      throw new UnreachableCaseError(category);
  }
}

export function adaptProjectDataNodesForFxReferences(
  workspaceId: string,
  category: ProjectDataTreeCategory,
  nodes: TreeNodeDefinition<unknown>[]
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const categoryRootId = getProjectDataCategoryRootId(category);
  const categoryParentId = referenceCategoryId(workspaceId, category);

  return nodes
    .filter((node) => node.id !== categoryRootId)
    .map((node) => {
      const { kind, missionId, refTrn, loadItemType, showVisibilityToggle } =
        getFxReferenceNodeMeta(category, node);

      return {
        id: scopedReferenceNodeId(workspaceId, category, node.id),
        label: node.label,
        parentId: scopedReferenceParentId(
          workspaceId,
          category,
          node.parentId,
          categoryParentId,
          categoryRootId
        ),
        isLeaf: node.isLeaf,
        labelIcon: node.labelIcon,
        labelSvgIcon: node.labelSvgIcon,
        sortPriority: node.sortPriority,
        toggleTooltip: node.toggleTooltip,
        dataTestId: node.dataTestId,
        showVisibilityToggle,
        data: {
          kind,
          workspaceId,
          category,
          ...(missionId !== undefined ? { missionId } : {}),
          ...(refTrn !== undefined ? { refTrn } : {}),
          ...(loadItemType !== undefined ? { loadItemType } : {}),
        },
      } satisfies TreeNodeDefinition<FxWorkspaceTreeItem>;
    });
}

function mapWorkspaceReferenceData(
  fxWorkspaceId: string,
  node: TreeNodeDefinition<WorkspaceTreeItem>
): FxWorkspaceTreeItem {
  const category = FX_MAP_WORKSPACES_REFERENCE_CATEGORY;
  const data = node.data;

  if (data?.kind === 'workspace') {
    return {
      kind: 'reference',
      workspaceId: fxWorkspaceId,
      category,
      refTrn: data.workspaceId,
    };
  }

  if (data?.kind === 'layer') {
    return {
      kind: 'reference',
      workspaceId: fxWorkspaceId,
      category,
      refTrn: node.id,
      mapWorkspaceId: data.workspaceId,
      ...(data.layerId !== undefined ? { layerId: data.layerId } : {}),
    };
  }

  return {
    kind: 'referenceFolder',
    workspaceId: fxWorkspaceId,
    category,
  };
}

export function adaptMapWorkspaceNodesForFxReferences(
  workspaceId: string,
  nodes: TreeNodeDefinition<WorkspaceTreeItem>[]
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const category = FX_MAP_WORKSPACES_REFERENCE_CATEGORY;
  const categoryParentId = referenceCategoryId(workspaceId, category);

  return nodes
    .filter((node) => node.id !== MAP_WORKSPACES_ROOT_NODE_ID)
    .map((node) => {
      const isLayer = node.data?.kind === 'layer';

      return {
        id: scopedReferenceNodeId(workspaceId, category, node.id),
        label: node.label,
        parentId: scopedReferenceParentId(
          workspaceId,
          category,
          node.parentId,
          categoryParentId,
          MAP_WORKSPACES_ROOT_NODE_ID
        ),
        isLeaf: node.isLeaf,
        labelIcon: node.labelIcon,
        labelSvgIcon: node.labelSvgIcon,
        sortPriority: node.sortPriority,
        toggleTooltip: node.toggleTooltip,
        dataTestId: node.dataTestId,
        visibleContent: node.visibleContent,
        showVisibilityToggle: isLayer ? undefined : false,
        data: mapWorkspaceReferenceData(workspaceId, node),
      } satisfies TreeNodeDefinition<FxWorkspaceTreeItem>;
    });
}
